import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  BadgeDollarSign,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  FileText,
  FolderTree,
  Hash,
  ImageIcon,
  Layers3,
  ListPlus,
  Loader2,
  Play,
  Ruler,
  Star,
  Trash2,
  UtensilsCrossed,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getApiValidationErrors, productsApi } from '../../api';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useConfirm } from '../../components/feedback/ConfirmProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import translateApiError from '../../utils/translateApiError';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import '../../styles/Products/product-details.css';

const LANGUAGE_LOCALES = {
  ar: 'ar-MA',
  fr: 'fr-FR',
  en: 'en-US',
  es: 'es-ES',
};

const isVideoUrl = (url = '') => {
  const cleanUrl = String(url)
    .split('?')[0]
    .toLowerCase();

  return (
    cleanUrl.endsWith('.mp4') ||
    cleanUrl.endsWith('.webm') ||
    cleanUrl.endsWith('.mov') ||
    cleanUrl.endsWith('.m4v') ||
    cleanUrl.endsWith('.ogg') ||
    cleanUrl.endsWith('.ogv')
  );
};

const getMediaUrl = (media) => {
  return resolveMediaUrl(
    media?.url ||
      media?.image_url ||
      media?.media_url ||
      media?.video_url ||
      media?.path ||
      media?.image_path ||
      media?.video_path ||
      '',
  );
};

const isVideoMedia = (media) => {
  const mediaType = String(
    media?.type ||
      media?.mime_type ||
      media?.media_type ||
      media?.file_type ||
      '',
  ).toLowerCase();

  if (mediaType.startsWith('video/')) {
    return true;
  }

  if (
    mediaType === 'video' ||
    mediaType === 'movie'
  ) {
    return true;
  }

  return isVideoUrl(
    getMediaUrl(media),
  );
};

function CustomSelect({
  value,
  options = [],
  onChange,
  disabled = false,
  ariaLabel,
  className = '',
  variant = '',
}) {
  const rootRef = useRef(null);
  const [open, setOpen] =
    useState(false);

  useEffect(() => {
    const handleOutsideClick = (
      event,
    ) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(
          event.target,
        )
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );

    document.addEventListener(
      'keydown',
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );

      document.removeEventListener(
        'keydown',
        handleEscape,
      );
    };
  }, []);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;

  return (
    <div
      ref={rootRef}
      className={[
        'product-details-custom-select',
        className,
        variant
          ? `product-details-custom-select--${variant}`
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className={`product-details-custom-select__trigger ${
          open ? 'is-open' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            setOpen(
              (current) =>
                !current,
            );
          }
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span>
          {selectedOption?.label ||
            '—'}
        </span>

        {disabled ? (
          <Loader2
            size={16}
            className="product-details-custom-select__loader"
          />
        ) : (
          <ChevronDown
            size={16}
            className={
              open
                ? 'is-rotated'
                : ''
            }
          />
        )}
      </button>

      {open ? (
        <div
          className="product-details-custom-select__menu"
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map(
            (option) => {
              const active =
                String(
                  option.value,
                ) === String(value);

              return (
                <button
                  key={
                    option.value
                  }
                  type="button"
                  role="option"
                  aria-selected={
                    active
                  }
                  className={
                    active
                      ? 'is-active'
                      : ''
                  }
                  onClick={() => {
                    onChange?.(
                      option.value,
                    );

                    setOpen(false);
                  }}
                >
                  <span>
                    {
                      option.label
                    }
                  </span>

                  {active ? (
                    <Check
                      size={15}
                    />
                  ) : null}
                </button>
              );
            },
          )}
        </div>
      ) : null}
    </div>
  );
}

function ProductDetails() {
  const { productId } =
    useParams();

  const navigate =
    useNavigate();

  const {
    t,
    language,
  } = useLanguage();

  const { showAlert } =
    useAlerts();

  const { confirm } =
    useConfirm();

  const [product, setProduct] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [
    updatingStatus,
    setUpdatingStatus,
  ] = useState(false);

  const [error, setError] =
    useState('');

  const [
    validationErrors,
    setValidationErrors,
  ] = useState({});

  const [
    activeMediaIndex,
    setActiveMediaIndex,
  ] = useState(0);

  const locale =
    LANGUAGE_LOCALES[
      language
    ] || 'fr-FR';

  const images =
    Array.isArray(
      product?.images,
    )
      ? product.images
      : [];

  const sizes =
    Array.isArray(
      product?.sizes,
    )
      ? product.sizes
      : [];

  const supplements =
    Array.isArray(
      product?.supplements,
    )
      ? product.supplements
      : [];

  const productName =
    useMemo(() => {
      return getTranslatedField(
        product,
        'name',
        language,
        'fr',
      );
    }, [
      product,
      language,
    ]);

  const productDescription =
    useMemo(() => {
      return getTranslatedField(
        product,
        'description',
        language,
        'fr',
      );
    }, [
      product,
      language,
    ]);

  const productIngredients =
    useMemo(() => {
      return getTranslatedField(
        product,
        'ingredients',
        language,
        'fr',
      );
    }, [
      product,
      language,
    ]);

  const categoryName =
    useMemo(() => {
      return getTranslatedField(
        product?.category,
        'name',
        language,
        'fr',
      );
    }, [
      product,
      language,
    ]);

  const parentCategoryName =
    useMemo(() => {
      return getTranslatedField(
        product?.category
          ?.parent,
        'name',
        language,
        'fr',
      );
    }, [
      product,
      language,
    ]);

  const mediaItems =
    useMemo(() => {
      return [...images]
        .filter((media) =>
          Boolean(
            getMediaUrl(
              media,
            ),
          ),
        )
        .sort(
          (
            firstMedia,
            secondMedia,
          ) => {
            if (
              firstMedia.is_primary &&
              !secondMedia.is_primary
            ) {
              return -1;
            }

            if (
              !firstMedia.is_primary &&
              secondMedia.is_primary
            ) {
              return 1;
            }

            return (
              Number(
                firstMedia.sort_order ||
                  0,
              ) -
              Number(
                secondMedia.sort_order ||
                  0,
              )
            );
          },
        );
    }, [images]);

  useEffect(() => {
    setActiveMediaIndex(0);
  }, [productId]);

  useEffect(() => {
    if (
      activeMediaIndex >
      mediaItems.length - 1
    ) {
      setActiveMediaIndex(0);
    }
  }, [
    activeMediaIndex,
    mediaItems.length,
  ]);

  const activeMedia =
    mediaItems[
      activeMediaIndex
    ] || null;

  const activeMediaUrl =
    getMediaUrl(
      activeMedia,
    );

  const activeMediaIsVideo =
    isVideoMedia(
      activeMedia,
    );

  const hasMultipleMedia =
    mediaItems.length > 1;

  const showPreviousMedia =
    () => {
      if (!hasMultipleMedia) {
        return;
      }

      setActiveMediaIndex(
        (currentIndex) =>
          currentIndex === 0
            ? mediaItems.length -
              1
            : currentIndex - 1,
      );
    };

  const showNextMedia = () => {
    if (!hasMultipleMedia) {
      return;
    }

    setActiveMediaIndex(
      (currentIndex) =>
        currentIndex ===
        mediaItems.length - 1
          ? 0
          : currentIndex + 1,
    );
  };

  const defaultSize =
    useMemo(() => {
      return (
        sizes.find(
          (size) =>
            size.is_default,
        ) ||
        sizes[0] ||
        null
      );
    }, [sizes]);

  const formatPrice = (
    value,
  ) => {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return '-';
    }

    const numericValue =
      Number(value);

    if (
      Number.isNaN(
        numericValue,
      )
    ) {
      return '-';
    }

    return numericValue.toLocaleString(
      locale,
      {
        style: 'currency',
        currency: 'MAD',
      },
    );
  };

  const getSizeName = (
    size,
  ) => {
    return getTranslatedField(
      size,
      'name',
      language,
      'fr',
    );
  };

  const getSupplementName = (
    supplement,
  ) => {
    return getTranslatedField(
      supplement,
      'name',
      language,
      'fr',
    );
  };

  const getMainPriceLabel =
    () => {
      if (
        product?.pricing_type ===
        'sizes'
      ) {
        if (!defaultSize) {
          return t(
            'products.pricing.noPrice',
          );
        }

        return `${t(
          'products.pricing.from',
        )} ${formatPrice(
          defaultSize.discount_price ||
            defaultSize.price,
        )}`;
      }

      return formatPrice(
        product?.discount_price ||
          product?.base_price,
      );
    };

  const fetchProduct =
    async () => {
      try {
        setLoading(true);
        setError('');
        setValidationErrors(
          {},
        );

        const response =
          await productsApi.getProduct(
            productId,
          );

        setProduct(
          response.data?.data ||
            null,
        );
      } catch (apiError) {
        const translatedError =
          translateApiError(
            apiError,
            t,
          );

        setProduct(null);
        setError(
          translatedError,
        );

        setValidationErrors(
          getApiValidationErrors(
            apiError,
          ),
        );

        showAlert({
          type: 'error',
          title: t(
            'alerts.error',
          ),
          message:
            translatedError,
        });
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchProduct();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleStatusChange =
    async (newStatus) => {
      if (
        !product ||
        newStatus ===
          product.status
      ) {
        return;
      }

      try {
        setUpdatingStatus(
          true,
        );

        await productsApi.updateAvailability(
          productId,
          newStatus,
        );

        setProduct(
          (currentProduct) => ({
            ...currentProduct,
            status: newStatus,
          }),
        );

        showAlert({
          type: 'success',
          title: t(
            'alerts.success',
          ),
          message: t(
            'products.alerts.availabilityUpdated',
          ),
        });
      } catch (apiError) {
        const translatedError =
          translateApiError(
            apiError,
            t,
          );

        setError(
          translatedError,
        );

        showAlert({
          type: 'error',
          title: t(
            'alerts.error',
          ),
          message:
            translatedError,
        });
      } finally {
        setUpdatingStatus(
          false,
        );
      }
    };

  const handleDelete =
    async () => {
      const confirmed =
        await confirm({
          title: t(
            'products.actions.delete',
          ),
          message: t(
            'products.confirmDelete',
          ),
          itemName:
            productName ||
            t(
              'products.noName',
            ),
          confirmText: t(
            'products.actions.delete',
          ),
          cancelText: t(
            'common.cancel',
          ),
          closeText: t(
            'common.close',
          ),
          variant: 'danger',
        });

      if (!confirmed) {
        return;
      }

      try {
        await productsApi.deleteProduct(
          productId,
        );

        showAlert({
          type: 'success',
          title: t(
            'alerts.success',
          ),
          message: t(
            'products.alerts.deleted',
          ),
        });

        navigate('/products');
      } catch (apiError) {
        const translatedError =
          translateApiError(
            apiError,
            t,
          );

        setError(
          translatedError,
        );

        setValidationErrors(
          getApiValidationErrors(
            apiError,
          ),
        );

        showAlert({
          type: 'error',
          title: t(
            'alerts.error',
          ),
          message:
            translatedError,
        });
      }
    };

  if (loading) {
    return (
      <main className="product-details-page">
        <section className="product-details-state-panel">
          <div className="product-details-loading">
            <Loader2
              size={30}
            />

            <span>
              {t(
                'products.loading',
              )}
            </span>
          </div>
        </section>
      </main>
    );
  }

  if (
    error ||
    !product
  ) {
    return (
      <main className="product-details-page">
        <section className="product-details-error-panel">
          <div className="product-details-error-panel__copy">
            <UtensilsCrossed
              size={36}
            />

            <h1>
              {t(
                'products.details.notFound',
              )}
            </h1>

            {error ? (
              <p>
                {error}
              </p>
            ) : null}

            {validationErrors
              .product ? (
              <p>
                {
                  validationErrors
                    .product[0]
                }
              </p>
            ) : null}
          </div>

          <Link
            to="/products"
            className="product-details-back-button"
          >
            <ArrowLeft
              size={18}
              className="product-details-back-icon"
            />

            <span>
              {t(
                'products.form.backToProducts',
              )}
            </span>
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="product-details-page">
      <header className="product-details-header">
        <Link
          to="/products"
          className="product-details-back-button"
        >
          <ArrowLeft
            size={18}
            className="product-details-back-icon"
          />

          <span>
            {t(
              'products.form.backToProducts',
            )}
          </span>
        </Link>

        <div className="product-details-header__main">
          <div className="product-details-header__copy">
            <p className="product-details-eyebrow">
              {t(
                'products.eyebrow',
              )}
            </p>

            <h1>
              {productName ||
                t(
                  'products.noName',
                )}
            </h1>

            <p className="product-details-header__description">
              {productDescription ||
                t(
                  'products.noDescription',
                )}
            </p>
          </div>

          <div className="product-details__actions">
            <Link
              to={`/products/${product.id}/edit`}
              className="product-details-primary-button"
            >
              <Edit size={18} />

              <span>
                {t(
                  'products.actions.edit',
                )}
              </span>
            </Link>

            <button
              type="button"
              className="product-details-secondary-button product-details-delete-button"
              onClick={
                handleDelete
              }
            >
              <Trash2
                size={18}
              />

              <span>
                {t(
                  'products.actions.delete',
                )}
              </span>
            </button>
          </div>
        </div>
      </header>

      <section className="product-details-grid">
        <article className="product-details-card product-details-card--media">
          <div className="product-details-main-media">
            {activeMediaUrl ? (
              activeMediaIsVideo ? (
                <video
                  key={
                    activeMedia.id ||
                    activeMediaUrl
                  }
                  src={
                    activeMediaUrl
                  }
                  controls
                  playsInline
                  preload="metadata"
                >
                  <track
                    kind="captions"
                  />
                </video>
              ) : (
                <img
                  key={
                    activeMedia.id ||
                    activeMediaUrl
                  }
                  src={
                    activeMediaUrl
                  }
                  alt={
                    activeMedia?.alt_text ||
                    productName ||
                    t(
                      'products.noName',
                    )
                  }
                />
              )
            ) : (
              <div className="product-details-media-placeholder">
                <span>
                  <UtensilsCrossed
                    size={38}
                  />
                </span>

                <strong>
                  {t(
                    'products.form.images',
                  )}
                </strong>
              </div>
            )}

            {activeMediaUrl ? (
              <span className="product-details-media-type">
                {activeMediaIsVideo ? (
                  <>
                    <Play
                      size={14}
                      fill="currentColor"
                    />

                    {t(
                      'products.details.video',
                    )}
                  </>
                ) : (
                  <>
                    <ImageIcon
                      size={14}
                    />

                    {t(
                      'products.details.photo',
                    )}
                  </>
                )}
              </span>
            ) : null}

            {hasMultipleMedia ? (
              <>
                <button
                  type="button"
                  className="product-details-media-arrow product-details-media-arrow--previous"
                  onClick={
                    showPreviousMedia
                  }
                  aria-label={t(
                    'products.details.previousMedia',
                  )}
                >
                  <ChevronLeft
                    size={22}
                  />
                </button>

                <button
                  type="button"
                  className="product-details-media-arrow product-details-media-arrow--next"
                  onClick={
                    showNextMedia
                  }
                  aria-label={t(
                    'products.details.nextMedia',
                  )}
                >
                  <ChevronRight
                    size={22}
                  />
                </button>
              </>
            ) : null}

            {mediaItems.length >
            0 ? (
              <span className="product-details-media-counter">
                {activeMediaIndex +
                  1}{' '}
                /{' '}
                {
                  mediaItems.length
                }
              </span>
            ) : null}
          </div>

          {mediaItems.length >
          0 ? (
            <div className="product-details-gallery">
              {mediaItems.map(
                (
                  media,
                  index,
                ) => {
                  const mediaUrl =
                    getMediaUrl(
                      media,
                    );

                  const video =
                    isVideoMedia(
                      media,
                    );

                  const mediaLabel =
                    video
                      ? t(
                          'products.details.video',
                        )
                      : t(
                          'products.details.photo',
                        );

                  return (
                    <button
                      type="button"
                      className={`product-details-gallery__item ${
                        activeMediaIndex ===
                        index
                          ? 'is-active'
                          : ''
                      }`}
                      key={
                        media.id ||
                        mediaUrl
                      }
                      onClick={() =>
                        setActiveMediaIndex(
                          index,
                        )
                      }
                      aria-label={`${mediaLabel} ${
                        index + 1
                      }`}
                    >
                      {video ? (
                        <span className="product-details-gallery__video-thumb">
                          <video
                            src={
                              mediaUrl
                            }
                            muted
                            playsInline
                            preload="metadata"
                            aria-hidden="true"
                          />

                          <span>
                            <Play
                              size={
                                15
                              }
                              fill="currentColor"
                            />
                          </span>
                        </span>
                      ) : mediaUrl ? (
                        <img
                          src={
                            mediaUrl
                          }
                          alt={
                            media.alt_text ||
                            productName ||
                            t(
                              'products.noName',
                            )
                          }
                        />
                      ) : (
                        <ImageIcon
                          size={20}
                        />
                      )}
                    </button>
                  );
                },
              )}
            </div>
          ) : null}
        </article>

        <article className="product-details-card product-details-card--summary">
          <div className="product-details-summary-head">
            <div>
              <span>
                {t(
                  'products.details.pricingType',
                )}
              </span>

              <h2>
                {productName ||
                  t(
                    'products.noName',
                  )}
              </h2>
            </div>

            <CustomSelect
              value={
                product.status
              }
              onChange={
                handleStatusChange
              }
              disabled={
                updatingStatus
              }
              ariaLabel={t(
                'products.form.status',
              )}
              className="product-status-select"
              variant={
                product.status
              }
              options={[
                {
                  value:
                    'available',
                  label: t(
                    'products.status.available',
                  ),
                },
                {
                  value:
                    'sold_out',
                  label: t(
                    'products.status.sold_out',
                  ),
                },
                {
                  value:
                    'hidden',
                  label: t(
                    'products.status.hidden',
                  ),
                },
              ]}
            />
          </div>

          <div className="product-details-metric-grid">
            <article>
              <span>
                <BadgeDollarSign
                  size={19}
                />
              </span>

              <div>
                <small>
                  {t(
                    'products.table.price',
                  )}
                </small>

                <strong>
                  {
                    getMainPriceLabel()
                  }
                </strong>
              </div>
            </article>

            <article>
              <span>
                <Ruler
                  size={19}
                />
              </span>

              <div>
                <small>
                  {t(
                    'products.details.sizes',
                  )}
                </small>

                <strong>
                  {sizes.length}
                </strong>
              </div>
            </article>
          </div>

          {product.is_featured ? (
            <div className="product-featured-badge">
              <Star
                size={15}
              />

              <span>
                {t(
                  'products.form.isFeatured',
                )}
              </span>
            </div>
          ) : null}

          <div className="product-details-info-list">
            <div>
              <span>
                <FolderTree
                  size={18}
                />
              </span>

              <div>
                <small>
                  {t(
                    'products.form.category',
                  )}
                </small>

                <strong>
                  {parentCategoryName
                    ? `${parentCategoryName} / ${categoryName}`
                    : categoryName ||
                      t(
                        'products.form.noCategory',
                      )}
                </strong>
              </div>
            </div>

            <div>
              <span>
                <Layers3
                  size={18}
                />
              </span>

              <div>
                <small>
                  {t(
                    'products.details.pricingType',
                  )}
                </small>

                <strong>
                  {product.pricing_type ===
                  'sizes'
                    ? t(
                        'products.pricing.sizes',
                      )
                    : t(
                        'products.pricing.fixed',
                      )}
                </strong>
              </div>
            </div>

            {product.pricing_type ===
              'fixed' &&
            product.discount_price ? (
              <div>
                <span>
                  <BadgeDollarSign
                    size={18}
                  />
                </span>

                <div>
                  <small>
                    {t(
                      'products.form.basePrice',
                    )}
                  </small>

                  <strong>
                    {formatPrice(
                      product.base_price,
                    )}
                  </strong>
                </div>
              </div>
            ) : null}

            <div>
              <span>
                <Hash size={18} />
              </span>

              <div>
                <small>
                  {t(
                    'products.form.sortOrder',
                  )}
                </small>

                <strong>
                  {product.sort_order ??
                    0}
                </strong>
              </div>
            </div>

            <div>
              <span>
                <Layers3
                  size={18}
                />
              </span>

              <div>
                <small>
                  {t(
                    'products.details.slug',
                  )}
                </small>

                <strong
                  dir="ltr"
                  className="product-details-ltr-value"
                >
                  {product.slug ||
                    '-'}
                </strong>
              </div>
            </div>
          </div>
        </article>
      </section>

      {product.pricing_type ===
      'sizes' ? (
        <section className="product-details-card product-details-section">
          <div className="product-details-section__head">
            <div>
              <span className="product-details-section__icon">
                <Ruler
                  size={20}
                />
              </span>

              <div>
                <h2>
                  {t(
                    'products.details.sizes',
                  )}
                </h2>

                <p>
                  {t(
                    'products.form.sizesDescription',
                  )}
                </p>
              </div>
            </div>

            <strong>
              {sizes.length}
            </strong>
          </div>

          {sizes.length > 0 ? (
            <div className="product-details-options-grid">
              {sizes.map(
                (size) => (
                  <article
                    className="product-details-option"
                    key={
                      size.id
                    }
                  >
                    <span>
                      <Ruler
                        size={
                          18
                        }
                      />
                    </span>

                    <div>
                      <strong>
                        {getSizeName(
                          size,
                        ) ||
                          t(
                            'products.form.sizeName',
                          )}
                      </strong>

                      <p>
                        {size.discount_price ? (
                          <small>
                            {formatPrice(
                              size.price,
                            )}
                          </small>
                        ) : null}

                        <b>
                          {formatPrice(
                            size.discount_price ||
                              size.price,
                          )}
                        </b>
                      </p>
                    </div>

                    {size.is_default ? (
                      <em>
                        {t(
                          'products.form.defaultSize',
                        )}
                      </em>
                    ) : null}
                  </article>
                ),
              )}
            </div>
          ) : (
            <div className="product-details-empty-block">
              {t(
                'products.details.noSizes',
              )}
            </div>
          )}
        </section>
      ) : null}

      {product.allow_supplements ? (
        <section className="product-details-card product-details-section">
          <div className="product-details-section__head">
            <div>
              <span className="product-details-section__icon">
                <ListPlus
                  size={20}
                />
              </span>

              <div>
                <h2>
                  {t(
                    'products.details.supplements',
                  )}
                </h2>

                <p>
                  {t(
                    'products.form.supplementsDescription',
                  )}
                </p>
              </div>
            </div>

            <strong>
              {
                supplements.length
              }
            </strong>
          </div>

          {supplements.length >
          0 ? (
            <div className="product-details-options-grid">
              {supplements.map(
                (
                  supplement,
                ) => (
                  <article
                    className="product-details-option"
                    key={
                      supplement.id
                    }
                  >
                    <span>
                      <ListPlus
                        size={
                          18
                        }
                      />
                    </span>

                    <div>
                      <strong>
                        {getSupplementName(
                          supplement,
                        ) ||
                          t(
                            'products.form.supplement',
                          )}
                      </strong>

                      <p>
                        <b>
                          +{' '}
                          {formatPrice(
                            supplement.price,
                          )}
                        </b>
                      </p>
                    </div>

                    {supplement.is_default ? (
                      <em>
                        {t(
                          'products.form.defaultSupplement',
                        )}
                      </em>
                    ) : null}
                  </article>
                ),
              )}
            </div>
          ) : (
            <div className="product-details-empty-block">
              {t(
                'products.details.noSupplements',
              )}
            </div>
          )}
        </section>
      ) : null}

      <section className="product-details-card product-details-section">
        <div className="product-details-section__head">
          <div>
            <span className="product-details-section__icon">
              <FileText
                size={20}
              />
            </span>

            <div>
              <h2>
                {t(
                  'products.form.description',
                )}
              </h2>
            </div>
          </div>
        </div>

        <div className="product-details-text-grid">
          <article>
            <span>
              {t(
                'products.form.description',
              )}
            </span>

            <p>
              {productDescription ||
                t(
                  'products.noDescription',
                )}
            </p>
          </article>

          <article>
            <span>
              {t(
                'products.form.ingredients',
              )}
            </span>

            <p>
              {productIngredients ||
                '-'}
            </p>
          </article>
        </div>
      </section>
    </main>
  );
}

export default ProductDetails;